import React, { useState, useRef, useEffect } from "react"
import Cropper from "react-cropper"
import PropTypes from "prop-types"

import Popup from "../Popup/Popup"
import styles from "./AddProfilePhotoPopup.module.scss"
import {
  DEFAULT_PROFILE_IMAGE as defaultUserAvatar,
  isUsableProfileImage,
} from "../../helpers/profileImage"
import {
  readFile,
  getRoundedCanvas,
  resizedImage,
  createImage,
  loadExistingProfileImage,
} from "../../helpers/imageHelpers"
import { objectComparison, throttle } from "../../helpers"
import Button from "../Button/Button"
import Loading from "../Loading/Loading"
import useWindowHeightWidth from "../../hooks/useWindowHeightWidth"

const getCropSize = (image, viewportWidth) => {
  if (viewportWidth >= 445) {
    return { height: image.height, width: image.width }
  }
  return { height: image.height, width: viewportWidth - 48 }
}

const AddProfilePhotoPopup = ({
  url,
  fallbackUrl,
  onClose,
  onSave,
  currentData,
}) => {
  const allowedTypes = ["png", "jpg", "jpeg"]
  const maxSize = 10000000 // 10 mb
  const { width } = useWindowHeightWidth()
  const candidateUrls = [url, fallbackUrl].filter(
    (src, index, list) => isUsableProfileImage(src) && list.indexOf(src) === index,
  )
  const hasExistingPhoto = candidateUrls.length > 0

  const [error, setError] = useState("")
  const [loaded, setLoaded] = useState(!hasExistingPhoto)
  const [file, setFile] = useState("")
  const [cropData, setCropData] = useState({})
  const [imageSize, setImageSize] = useState()
  const [croppedImage, setCroppedImage] = useState("")
  const [uploadProgress, setUploadProgress] = useState(0)
  const [isUploading, setIsUploading] = useState(false)

  const inputRef = useRef(null)
  const cropper = useRef(null)
  const userSelectedNewFile = useRef(false)
  const currentDataRef = useRef(currentData)

  const acceptButtonName = file ? "Upload" : "Choose photo"

  useEffect(() => {
    currentDataRef.current = currentData
  }, [currentData])

  useEffect(() => {
    if (userSelectedNewFile.current) return

    let cancelled = false

    const loadExistingPhoto = async () => {
      if (!candidateUrls.length) {
        setFile("")
        setImageSize(undefined)
        setLoaded(true)
        return
      }

      setLoaded(false)

      for (const src of candidateUrls) {
        try {
          const imageEl = await loadExistingProfileImage(src)
          if (cancelled) return

          const data = currentDataRef.current || {}
          setImageSize(getCropSize(imageEl, width))
          setFile(imageEl.src)
          setCropData({
            x: Number(data.x) || 0,
            y: Number(data.y) || 0,
            width: Number(data.width) || 0,
            height: Number(data.height) || 0,
            rotate: 0,
          })
          setLoaded(true)
          return
        } catch (err) {
          // Try the next candidate URL
        }
      }

      if (cancelled) return
      setFile("")
      setImageSize(undefined)
      setLoaded(true)
    }

    loadExistingPhoto()

    return () => {
      cancelled = true
    }
  }, [url, fallbackUrl, width])

  const choosePhotoHandler = async () => {
    if (!file) {
      inputRef.current.click()
      return
    }

    setIsUploading(true)
    setUploadProgress(1)

    const handleProgress = (progress) => {
      setUploadProgress(Math.min(100, Math.max(1, Math.round(progress))))
    }

    try {
      await onSave({ file, croppedImage, cropData }, handleProgress)
      setIsUploading(false)
      setUploadProgress(100)
      onClose()
    } catch (err) {
      setError("Failed to upload photo. Please try again.")
      setIsUploading(false)
      setUploadProgress(0)
    }
  }

  const onFileChange = async ({ target }) => {
    const selected = target.files ? target.files[0] : null
    if (!selected) return

    const isValidType = allowedTypes.some((ext) => selected.type.includes(ext))

    if (selected.size > maxSize || !isValidType) {
      setError(
        "Sorry, there was a problem while uploading the photo. Please, try again.",
      )
      target.value = ""
      return
    }

    try {
      let imageDataUrl = await readFile(selected)
      imageDataUrl = await createImage(imageDataUrl)
      imageDataUrl = resizedImage(imageDataUrl)
      imageDataUrl = await createImage(imageDataUrl)

      userSelectedNewFile.current = true
      setError("")
      setImageSize(getCropSize(imageDataUrl, width))
      setFile(imageDataUrl.src)
      setLoaded(true)
    } catch (err) {
      setError(
        "Sorry, there was a problem while uploading the photo. Please, try again.",
      )
      setFile("")
      setImageSize(undefined)
      setLoaded(true)
    } finally {
      target.value = ""
    }
  }

  const _crop = () => {
    const crop = cropper.current
    if (!crop) return

    try {
      let oldCropData = {}
      let oldCroppedImage = {}

      if (!objectComparison(oldCropData, crop.getData(true))) {
        oldCropData = crop.getData(true)
        setCropData(crop.getData(true))
      }

      const canvas = crop.getCroppedCanvas()
      if (!canvas) return

      const rounded = getRoundedCanvas(canvas).toDataURL()
      if (!objectComparison(oldCroppedImage, rounded)) {
        oldCroppedImage = rounded
        setCroppedImage(rounded)
      }
    } catch (err) {
      // Canvas can be tainted if the remote image has no CORS headers.
    }
  }

  return (
    <Popup
      title="Upload Avatar"
      isCrossIcon
      button={
        <Button
          className={styles.btn}
          buttonStyle="outlinedBlack"
          height="40px"
          onClick={onClose}
          disabled={isUploading}
        >
          Cancel
        </Button>
      }
      acceptButton={
        <Button
          className={styles.btn}
          height="40px"
          buttonStyle="black"
          onClick={choosePhotoHandler}
          disabled={isUploading}
        >
          {acceptButtonName}
        </Button>
      }
      className={styles.addPhotoPopup}
      closeAction={onClose}
    >
      <div className={styles.contentWrapper}>
        <input
          ref={inputRef}
          type="file"
          accept=".png,.jpg,.jpeg"
          onChange={onFileChange}
        />

        {isUploading ? (
          <div className={styles.uploadProgressWrapper}>
            <Loading />
            <div className={styles.progressBarContainer}>
              <div className={styles.progressBar}>
                <div
                  className={styles.progressBarFill}
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className={styles.progressText}>{uploadProgress}%</p>
            </div>
          </div>
        ) : !loaded ? (
          <Loading />
        ) : file && imageSize ? (
          <div className={styles.cropWrapper}>
            <Cropper
              ref={cropper}
              src={file}
              style={{
                height: imageSize.height,
                width: imageSize.width,
                margin: "auto",
              }}
              data={cropData}
              aspectRatio={1 / 1}
              guides={true}
              viewMode={1}
              crop={throttle(_crop, 100)}
              background={false}
              scalable={false}
              zoomable={false}
              autoCrop={true}
              checkOrientation={false}
              checkCrossOrigin={false}
            />
            <p
              className={styles.changePhoto}
              onClick={() => inputRef.current.click()}
            >
              Choose another photo
            </p>
          </div>
        ) : (
          <>
            {error && <p className={styles.errorMessage}>{error}</p>}
            {!error && <p>Maximum 10 Mb. Only .png .jpg .jpeg files. </p>}
            <img
              className={styles.defaultImage}
              src={defaultUserAvatar}
              alt="User Profile Avatar"
              onClick={() => inputRef.current.click()}
            />
          </>
        )}
      </div>
    </Popup>
  )
}

AddProfilePhotoPopup.propTypes = {
  url: PropTypes.string,
  fallbackUrl: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  currentData: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
    rotate: PropTypes.number,
  }),
}

AddProfilePhotoPopup.defaultProps = {
  url: "",
  fallbackUrl: "",
}

export default AddProfilePhotoPopup
